import AnimatedImageSequence, { usePreloadingImageSequence } from '../../generic/sequence/AnimatedImageSequence';
import { DebugDeviceScroll, DebugDeviceScrollSpring, useDebug } from '../../../context/Debug';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { to, useSpring } from '@react-spring/web';

import DeviceSections from '../../Device/DeviceSections';
import ScrollProgress from '../../ScrollProgress';
import { constrain } from '../../../utils/constrain';
import { doMediaMatch } from '../../../utils/useMediaQueryChange';
import { isDevEnvironment } from '../../../utils/isDevEnvironment';
import { mapRange } from '../../../utils/mapRange';
import styles from './index.module.scss';
import { useRequestAnimFrameOnScroll } from '../../../context/onRequestAnimFrameOnScroll';

const teleportPageLimit = 2; // limit of number pages that can be jumped accross before triggering the 'teleport' fade out / in while animating the image sequence
const desktopCanvasSize = { width: 1920, height: 1080 };
const mobileCanvasSize = { width: 1080, height: 1080 };

export default function DeviceSequence({ pageData, productListRef, scrollPageHeight }) {
  let { registerCallback } = useRequestAnimFrameOnScroll();
  let debug = useDebug();

  let { sections } = pageData;
  let pageCount = sections?.length;
  let isMobile = doMediaMatch(`(max-width: 992px) and (orientation: portrait)`);
  // defer determination of 📱 vs 🖥 size to prevent prematurly setting the canvas size
  let [size, setSize] = useState(null); //? debug
  useEffect(() => setSize(isMobile ? mobileCanvasSize : desktopCanvasSize), [isMobile, setSize]);

  // 🎥 image sequence progression spring
  const pageNum = useRef(0);
  let totalPageCount = sections?.length || 0;
  let [scrollPositionSpring, setScrollPositionSpring] = useSpring(
    () => ({
      progress: 0,
      pageScroll: 0,
      pageProgress: 0,
      currentPageNum: 0,
      prevPageNum: 0, // together with currentPageNum, this is used to trigger state updates for the <DeviceSection>s

      totalPageCount,
      scrollPageHeight,
      pagesTotalHeight: 1,

      animationIndex: 0,
      prevAnimationIndex: 0,
      targetAnimationIndex: 0,

      scrollTeleportOpacity: 1, // base for fade out / in on long distance scroll position jumps
      sequenceCanvasFixed: true,
    }),
    [scrollPageHeight, totalPageCount] // [pageCount, registerCallback, scrollPageHeight, sections, totalPageCount, pageData]
  );

  // 🎞 resolve all image urls here:
  const imageUrls = useMemo(() => {
    let { animationUrl, animationUrlMobile, animationFrames } = pageData;
    animationUrl = isMobile ? animationUrlMobile : animationUrl;
    return Array.from(Array(animationFrames).keys(), index => getImageUrl(animationUrl, index));
  }, [isMobile, pageData]);

  let keyFrames = useMemo(() => getKeyFrames(pageData), [pageData]);
  let { cache } = usePreloadingImageSequence({ keyFrames, imageUrls });

  // 🖱️🌊🌊️🌊️️️🖱️ Everything starts here!
  useEffect(() => {
    /** 🎞 updates image sequence progression animation */
    function updateSequenceAnimationOnPageChange(targetPage, prevPage) {
      targetPage = constrain(targetPage, 0, pageCount - 1);
      let targetAnimationIndex = sections[targetPage].mainFrame;

      let departurePage = constrain(prevPage, 0, pageCount - 1);
      let destinationPage = constrain(targetPage, 0, pageCount - 1);

      let pageDiff = destinationPage - departurePage;
      let direction = pageDiff !== 0 ? pageDiff / Math.abs(pageDiff) : 0;

      let currentAnimationIndex = scrollPositionSpring.animationIndex.get();
      setScrollPositionSpring.start({
        prevAnimationIndex: currentAnimationIndex,
        targetAnimationIndex,
        immediate: true,
      });

      if (Math.abs(pageDiff) <= teleportPageLimit) {
        let duration = calcSequenceAnimDuration(currentAnimationIndex, targetAnimationIndex);
        setScrollPositionSpring.start({ animationIndex: targetAnimationIndex, config: { duration } });
      } else {
        setScrollPositionSpring.start({
          to: { animationIndex: targetAnimationIndex },
          config: { duration: 800 },
          from: { animationIndex: targetAnimationIndex - 14 * direction },
          delay: 500,
        });
      }
    }

    /** 🧞‍♀️ image sequence 'teleport' opacity transitions */
    function updateTeleportOpacity(targetPage, prevPage) {
      let departurePageNum = constrain(prevPage, 0, pageCount - 1);
      let destinationPageNum = constrain(targetPage, 0, pageCount - 1);
      let pageDiff = Math.abs(destinationPageNum - departurePageNum);
      if (pageDiff <= teleportPageLimit) return;
      setScrollPositionSpring.start({
        to: async next => {
          await next({ scrollTeleportOpacity: 0, config: { duration: 300 } });
          await next({ scrollTeleportOpacity: 1, config: { duration: 300 }, delay: 500 });
        },
      });
    }

    /** 🖱 progress update - called with requestAnimationFrame on scroll & resize events */
    function updateScrollPosition(doc) {
      const firstPageHeight = 150;
      const pagesTotalHeight = firstPageHeight + totalPageCount * scrollPageHeight;
      let progress = mapRange(doc.scrollTop, [0, pagesTotalHeight], [0, 1]);

      let currentPageNum, pageScroll, pageProgress;
      if (doc.scrollTop < firstPageHeight) {
        currentPageNum = 0;
        pageProgress = mapRange(doc.scrollTop, [0, firstPageHeight], [0, 1]);
        pageScroll = doc.scrollTop;
      } else {
        currentPageNum = 1 + Math.floor((doc.scrollTop - firstPageHeight) / scrollPageHeight);
        pageScroll = (doc.scrollTop - firstPageHeight) % scrollPageHeight;
        pageProgress = pageScroll / scrollPageHeight;
      }

      if (currentPageNum !== pageNum.current) {
        setScrollPositionSpring.start({ sequenceCanvasFixed: currentPageNum < totalPageCount, immediate: true });
        let prevPageNum = pageNum.current;
        pageNum.current = currentPageNum;

        setScrollPositionSpring.start({ currentPageNum, prevPageNum, immediate: true });
        updateSequenceAnimationOnPageChange(currentPageNum, prevPageNum);
        updateTeleportOpacity(currentPageNum, prevPageNum);
      }

      setScrollPositionSpring.start({
        totalPageCount,
        scrollPageHeight,
        pagesTotalHeight,
        progress,
        pageScroll,
        pageProgress,
        immediate: true,
      });
    }

    updateScrollPosition(document.documentElement);
    const unregister = registerCallback(() => updateScrollPosition(document.documentElement)); // equivalent of window.addEventListener('scroll', onScroll); _and_ ('resize', ...)
    return () => unregister(); // equivalent of window.removeEventListener('scroll', onScroll); _and_ same for resize event
  });

  const jumpToSection = useCallback(
    event => {
      // This should be changed to the following - but the animation does not jump correctly yet:
      // navigate(`${location.origin}${location.pathname}#section${index}`);
      let index = event.index;
      if (index >= pageCount) {
        if (productListRef.current) {
          window.scrollTo(0, offset(productListRef.current).top);
        }
      } else if (index === 0) {
        window.scrollTo(0, 0);
      } else {
        // This should not be here, but what the heck:
        const firstPageHeight = 150;
        window.scrollTo(0, (index - 1) * scrollPageHeight + firstPageHeight);
      }
    },
    [pageCount, productListRef, scrollPageHeight]
  );

  // let imageSequenceCanvasFixed = false; //? let imageSequenceFixed = currentPageNum.get() >= pageCount;

  return (
    <>
      {debug && (
        <DebugDeviceScroll>
          <DebugDeviceScrollSpring spring={scrollPositionSpring} title="scrollPositionSpring" />
        </DebugDeviceScroll>
      )}
      <div className={styles.deviceSequence}>
        <AnimatedImageSequence
          imageUrls={imageUrls}
          imageCache={cache}
          size={size}
          scrollPositionSpring={scrollPositionSpring}
          //className={mergeClassNames(styles.imageSequence, imageSequenceCanvasFixed ? styles.fixed : '')}
        />
        <DeviceSections pageData={pageData} debug={debug} scrollSpring={scrollPositionSpring} />
      </div>

      <DeviceScrollProgress
        sections={sections}
        jumpToSection={jumpToSection}
        scrollPositionSpring={scrollPositionSpring}
      />
    </>
  );
}

function getImageUrl(baseUrl, index) {
  return baseUrl.replace('FRAME', String(index).padStart(4, '0'));
}

function offset(el) {
  let rect = el.getBoundingClientRect(),
    scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
    scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
}

function getKeyFrames(pageData) {
  // console.log('animationFrames: ', pageData.animationFrames); //debug: leaving this for later debugging
  let prev = 0;
  let keyFrames = pageData.sections.map((section, index) => {
    if (section.mainFrame < prev) {
      console.warn(
        `Main frames are not in the correct order at index: ${index}  mainFrame: ${section.mainFrame} ${section.heading}`
      );
    }
    prev = section.mainFrame;
    return section.mainFrame;
  });
  if (isDevEnvironment) console.log('Animation keys: ', keyFrames);
  return keyFrames;
}

/** calculate individual progress per section for progress bars in <ScrollProgress> */
function DeviceScrollProgress({ sections, jumpToSection, scrollPositionSpring }) {
  let { pageProgress, currentPageNum } = scrollPositionSpring;

  // rewrite to useSprings? (look at Workspace/<Features>-components)
  // let progressSprings = useSprings(sections.length, index => ({
  //   progress: 0,
  // }));

  let sectionsScrollProgresses = [
    ...sections?.map((section, index) => {
      let progressSpring = to([pageProgress, currentPageNum], (pageProgress, currentPageNum) => {
        let safePagePropress = typeof pageProgress === 'number' ? pageProgress : 0;
        let safeCurrentPage = typeof currentPageNum === 'number' ? currentPageNum : 0;
        return safePagePropress + safeCurrentPage - index + 1;
      });
      return { title: section.shortHeading || section.heading, index: index, progressSpring };
    }),
    { title: 'Products', index: sections.length, style: { fontWeight: 700, opacity: 1, pointerEvents: 'all' } },
  ];

  return <ScrollProgress sections={sectionsScrollProgresses} selectHandler={jumpToSection} />;
}

/**  📼 image sequence animation spring 📼 */
const ANIM_DURATION_MULTIPLIER = 15; // duration quantifier
const ANIM_DURATION_MAX = 2500; // tune down sequence anim duration on long distances
function calcSequenceAnimDuration(prevAnimationIndex, targetAnimationIndex) {
  let durationBasedOnDistance = Math.abs(prevAnimationIndex - targetAnimationIndex) * ANIM_DURATION_MULTIPLIER;
  let duration = Math.min(durationBasedOnDistance, ANIM_DURATION_MAX); // let duration = durationBasedOnDistance < ANIM_DURATION_MAX ? durationBasedOnDistance : 1000;
  return duration;
}
